import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { DatePicker, Divider, Form, Input, Select, Table, Button, Typography, message, Button as AntButton, Tooltip } from 'antd'
import { default as CustomButton } from 'components/Button'
import Icon from 'components/Icon'
import Page from '../../../components/Page'
import { PERMISSIONS, ICONS } from '../../../utils/constants'
import { syncCustomerBookletCoupons } from '../../../api/customers'
import useClient from 'hooks/useClient'
import ExportBookletsModal from 'components/ExportBookletsModal'
import {
  getBooklet,
  getBookletTransactionGroups,
  getBookletTransactions,
  setBooklet,
  setBookletTransactionGroups,
  setBookletTransactions,
  updateBooklet,
} from '../../../redux/actions/booklets'
import { getProducts } from '../../../api/products'
const { Option } = Select
const { RangePicker } = DatePicker
const { Title } = Typography

const BookletsView = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { isClientCan } = useClient()
  const [form] = Form.useForm()

  const canUpdateBooklets = isClientCan(PERMISSIONS.UPDATE_BOOKLETS)

  const {
    item: booklet,
    isGetLoading,
    transactionGroups,
    isTransactionGroupsLoading,
    transactionGroupsErrors,
    transactions,
    isTransactionsLoading,
    transactionsErrors,
  } = useSelector((state) => state.booklets)
  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [loadingCoupons, setLoadingCoupons] = useState(false)
  const [syncingUserId, setSyncingUserId] = useState(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isExportModalVisible, setIsExportModalVisible] = useState(false)

  // Transactions (admin): groups table
  const [groupsCurrentPage, setGroupsCurrentPage] = useState(1)
  const [groupsPageSize, setGroupsPageSize] = useState(10)
  const [groupsFilters, setGroupsFilters] = useState({
    customerCode: null,
    skuCode: null,
  })
  const [groupsFiltersForm] = Form.useForm()

  const [expandedGroupKey, setExpandedGroupKey] = useState(null)
  const [expandedGroup, setExpandedGroup] = useState(null)

  // Transactions (admin): expanded group details
  const [txCurrentPage, setTxCurrentPage] = useState(1)
  const [txPageSize, setTxPageSize] = useState(10)
  const [txTypeFilter, setTxTypeFilter] = useState('ALL')
  const [txValidFromRange, setTxValidFromRange] = useState(null)
  const [txValidToRange, setTxValidToRange] = useState(null)
  const [txRedeemAtRange, setTxRedeemAtRange] = useState(null)
  const [txSerialNumberInput, setTxSerialNumberInput] = useState('')
  const [txSerialNumber, setTxSerialNumber] = useState('')

  useEffect(() => {
    if (!id) return

    dispatch(getBooklet({ id }))

    return () => {
      dispatch(setBooklet({}))
      dispatch(
        setBookletTransactionGroups({
          result: [],
          total: 0,
        }),
      )
      dispatch(
        setBookletTransactions({
          result: [],
          total: 0,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!booklet?.id) return

    form.setFieldsValue({
      purchaseProductId: booklet.purchaseProduct?.id,
      redeemProductId: booklet.redeemProduct?.id,
    })
    setIsDirty(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booklet?.id])

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true)
        const data = await getProducts(500, 1)
        setProducts(data?.result || data || [])
      } catch {
        message.error('Failed to load products')
      } finally {
        setLoadingProducts(false)
      }
    }

    loadProducts()
  }, [])

  const onSubmit = (values) => {
    const data = {
      redeemProductId: values.redeemProductId,
    }

    dispatch(updateBooklet({ id, data, navigate }))
  }

  const onSubmitGroupsFilters = (values) => {
    setGroupsCurrentPage(1)
    setGroupsFilters({
      customerCode: values.customerCode || null,
      // skuCode: values.skuCode || null,
      skuCode: booklet?.purchaseProduct?.sapId
    })

    setExpandedGroupKey(null)
    setExpandedGroup(null)
    setTxCurrentPage(1)
    dispatch(
      setBookletTransactions({
        result: [],
        total: 0,
      }),
    )
  }

  useEffect(() => {
    if (!id || !booklet?.purchaseProduct?.sapId) return

    dispatch(
      getBookletTransactionGroups({
        bookletId: id,
        page: groupsCurrentPage,
        size: groupsPageSize,
        ...groupsFilters,
        skuCode: booklet?.purchaseProduct?.sapId,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, groupsCurrentPage, groupsPageSize, groupsFilters, booklet?.id])

  useEffect(() => {
    if (!id || !expandedGroup) return

    const validFromStart = txValidFromRange
      ? txValidFromRange[0].clone().startOf('day').toISOString()
      : null
    const validFromEnd = txValidFromRange ? txValidFromRange[1].clone().endOf('day').toISOString() : null

    const validToStart = txValidToRange ? txValidToRange[0].clone().startOf('day').toISOString() : null
    const validToEnd = txValidToRange ? txValidToRange[1].clone().endOf('day').toISOString() : null

    const redeemAtStart = txRedeemAtRange
      ? txRedeemAtRange[0].clone().startOf('day').toISOString()
      : null
    const redeemAtEnd = txRedeemAtRange ? txRedeemAtRange[1].clone().endOf('day').toISOString() : null

    dispatch(
      getBookletTransactions({
        bookletId: id,
        userId: expandedGroup.userId,
        skuCode: expandedGroup.skuCode,
        page: txCurrentPage,
        size: txPageSize,
        typeFilter: txTypeFilter,
        validFromStart,
        validFromEnd,
        validToStart,
        validToEnd,
        redeemAtStart,
        redeemAtEnd,
        serialNumber: txSerialNumber || null,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    expandedGroup,
    txCurrentPage,
    txPageSize,
    txTypeFilter,
    txValidFromRange,
    txValidToRange,
    txRedeemAtRange,
    txSerialNumber,
  ])

  const resetGroupsFilters = () => {
    groupsFiltersForm.resetFields()
    setGroupsCurrentPage(1)
    setGroupsFilters({
      customerCode: null,
      skuCode: booklet?.purchaseProduct?.sapId,
    })

    setExpandedGroupKey(null)
    setExpandedGroup(null)
    setTxCurrentPage(1)
    dispatch(
      setBookletTransactions({
        result: [],
        total: 0,
      }),
    )
  }

  const resetTransactionsFilters = () => {
    setTxCurrentPage(1)
    setTxTypeFilter('ALL')
    setTxValidFromRange(null)
    setTxValidToRange(null)
    setTxRedeemAtRange(null)
    setTxSerialNumberInput('')
    setTxSerialNumber('')
  }

  const handleSyncBookletCoupons = async (userId) => {
    if (!userId) return
    try {
      setSyncingUserId(userId)
      const res = await syncCustomerBookletCoupons(userId)
      message.success(res?.message || 'Coupon sync queued')
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || 'Coupon sync failed')
    } finally {
      setSyncingUserId(null)
    }
  }

  const groupItems = transactionGroups?.result || []
  const groupTotal = transactionGroups?.total || 0

  const txItems = transactions?.result || []
  const txTotal = transactions?.total || 0

  const hasGroupsError = Array.isArray(transactionGroupsErrors) && transactionGroupsErrors.length > 0
  const hasTransactionsError = Array.isArray(transactionsErrors) && transactionsErrors.length > 0

  if (!booklet && !isGetLoading) {
    return (
      <Page pageTitle={t('booklets.title', 'Booklets')}>
        <p>{t('notFound.text')}</p>
      </Page>
    )
  }

  if (!booklet) {
    return <Page isGetLoading={true} />
  }

  const groupRowKey = (record) => `${record.userId}-${record.skuCode || 'NO_SKU'}`

  const groupColumns = [
    {
      title: t('fields.customer code', 'Customer code'),
      dataIndex: 'customerCode',
      key: 'customerCode',
      render: (v) => v || '-',
      width: 140,
    },
    {
      title: t('fields.customer name', 'Customer name'),
      dataIndex: 'customerName',
      key: 'customerName',
      render: (v) => v || '-',
      width: 140,
    },
    // {
    //   title: t('fields.sku', 'SKU'),
    //   dataIndex: 'skuCode',
    //   key: 'skuCode',
    //   render: (v) => v || '-',
    // },
    {
      title: t('fields.total purchase', 'Total purchase'),
      dataIndex: 'totalPurchase',
      key: 'totalPurchase',
      align: 'center',
      width: 140,
    },
    {
      title: t('fields.total redeem', 'Total redeem'),
      dataIndex: 'totalRedeem',
      key: 'totalRedeem',
      align: 'center',
      width: 140,
    },
    {
      title: t('fields.total expired', 'Total expired'),
      dataIndex: 'totalExpired',
      key: 'totalExpired',
      align: 'center',
      width: 140,
    },
    {
      title: t('fields.actions', 'Actions'),
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_, record) => (
        <Tooltip title={t('customers.syncCoupons', 'Sync booklet')}>
          <Button
            type="link"
            icon={<Icon icon={ICONS.COUPON} size={18} />}
            loading={syncingUserId === record.userId}
            disabled={!!syncingUserId}
            onClick={(e) => {
              e.stopPropagation()
              handleSyncBookletCoupons(record.userId)
            }}
          />
        </Tooltip>
      ),
    },
  ]

  const txColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      render: (_, __, index) => index + 1 + txPageSize * (txCurrentPage - 1),
    },
    {
      title: t('fields.type', 'Type'),
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      width: 110,
    },
    {
      title: t('fields.order number', 'Order number'),
      key: 'order',
      render: (_, record) => {
        const ref = record.redeemAt ? record.redeemOrderId : record.orderNumber 
        if (!ref) return '-'

        if (record.orderId) {
          return (
            <Button type="link" onClick={() => navigate(`/main/orders/view/${record.orderId}`)}>
              {ref}
            </Button>
          )
        }

        return ref
      },
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (d) => (d ? d : '-'),
      filterDropdown: ({ confirm }) => (
        <>
          <div className={'container'}>
            <Input
              value={txSerialNumberInput}
              onChange={(e) => setTxSerialNumberInput(e.target.value)}
              placeholder={t('fields.serial number placeholder', 'Enter serial number')}
              allowClear
              style={{ width: 200 }}
            />
          </div>
          <div className={'ant-table-filter-dropdown-btns'}>
            <AntButton
              type={'link'}
              size={'small'}
              disabled={!txSerialNumberInput}
              onClick={() => {
                setTxCurrentPage(1)
                setTxSerialNumberInput('')
                setTxSerialNumber('')
              }}
            >
              Reset
            </AntButton>
            <AntButton
              type={'primary'}
              size={'small'}
              onClick={() => {
                setTxCurrentPage(1)
                setTxSerialNumber(txSerialNumberInput)
                confirm({ closeDropdown: true })
              }}
            >
              OK
            </AntButton>
          </div>
        </>
      ),
    },
    {
      title: t('fields.valid from', 'Valid from'),
      dataIndex: 'validFrom',
      key: 'validFrom',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD') : '-'),
      width: 120,
      filterDropdown: ({ confirm }) => (
        <>
          <div className={'container'}>
            <RangePicker
              value={txValidFromRange}
              onChange={(dates) => {
                setTxValidFromRange(dates ? [dates[0].startOf('day'), dates[1].endOf('day')] : null)
              }}
            />
          </div>
          <div className={'ant-table-filter-dropdown-btns'}>
            <AntButton
              type={'link'}
              size={'small'}
              disabled={!txValidFromRange}
              onClick={() => {
                setTxCurrentPage(1)
                setTxValidFromRange(null)
              }}
            >
              Reset
            </AntButton>
            <AntButton
              type={'primary'}
              size={'small'}
              onClick={() => {
                setTxCurrentPage(1)
                confirm({ closeDropdown: true })
              }}
            >
              OK
            </AntButton>
          </div>
        </>
      ),
    },
    {
      title: t('fields.valid to', 'Valid to'),
      dataIndex: 'validTo',
      key: 'validTo',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD') : '-'),
      width: 120,
      filterDropdown: ({ confirm }) => (
        <>
          <div className={'container'}>
            <RangePicker
              value={txValidToRange}
              onChange={(dates) => {
                setTxValidToRange(dates ? [dates[0].startOf('day'), dates[1].endOf('day')] : null)
              }}
            />
          </div>
          <div className={'ant-table-filter-dropdown-btns'}>
            <AntButton
              type={'link'}
              size={'small'}
              disabled={!txValidToRange}
              onClick={() => {
                setTxCurrentPage(1)
                setTxValidToRange(null)
              }}
            >
              Reset
            </AntButton>
            <AntButton
              type={'primary'}
              size={'small'}
              onClick={() => {
                setTxCurrentPage(1)
                confirm({ closeDropdown: true })
              }}
            >
              OK
            </AntButton>
          </div>
        </>
      ),
    },
    {
      title: t('fields.expired', 'Expired'),
      dataIndex: 'isExpired',
      key: 'isExpired',
      align: 'center',
      width: 100,
      render: (v, record) => {
        if (record.type !== 'PURCHASE') return '-'
        return v ? t('app.yes', 'Yes') : t('app.no', 'No')
      },
    },
    {
      title: t('fields.redeem at', 'Redeem at'),
      dataIndex: 'redeemAt',
      key: 'redeemAt',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : '-'),
      width: 160,
      filterDropdown: ({ confirm }) => (
        <>
          <div className={'container'}>
            <RangePicker
              value={txRedeemAtRange}
              onChange={(dates) => {
                setTxRedeemAtRange(dates ? [dates[0].startOf('day'), dates[1].endOf('day')] : null)
              }}
            />
          </div>
          <div className={'ant-table-filter-dropdown-btns'}>
            <AntButton
              type={'link'}
              size={'small'}
              disabled={!txRedeemAtRange}
              onClick={() => {
                setTxCurrentPage(1)
                setTxRedeemAtRange(null)
              }}
            >
              Reset
            </AntButton>
            <AntButton
              type={'primary'}
              size={'small'}
              onClick={() => {
                setTxCurrentPage(1)
                confirm({ closeDropdown: true })
              }}
            >
              OK
            </AntButton>
          </div>
        </>
      ),
    },
    {
      title: t('fields.created', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : '-'),
      width: 160,
    },
  ]

  const handleGroupsTableChange = (paginate) => {
    setGroupsCurrentPage(paginate.current)
    setGroupsPageSize(paginate.pageSize)

    setExpandedGroupKey(null)
    setExpandedGroup(null)
  }

  const handleTransactionsTableChange = (paginate) => {
    setTxCurrentPage(paginate.current)
    setTxPageSize(paginate.pageSize)
  }

  const title =
    booklet?.purchaseProduct?.title && booklet?.redeemProduct?.title
      ? `${booklet.purchaseProduct.title} → ${booklet.redeemProduct.title}`
      : t('booklets.title', 'Booklets')

  return (
    <Page pageTitle={title}>
      <div className="booklet-view">
        <div className="booklet-view__content">
          <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            onValuesChange={() => setIsDirty(true)}
            initialValues={{
              purchaseProductId: booklet.purchaseProduct?.id,
              redeemProductId: booklet.redeemProduct?.id,
            }}
          >
            <Form.Item
              label={t('booklets.field.purchaseProduct', 'Purchase product')}
              name="purchaseProductId"
              rules={[{ required: true, message: t('fields.required') }]}
            >
              <Select
                loading={loadingProducts}
                placeholder={t('fields.select product')}
                showSearch
                optionFilterProp="children"
                disabled={true}
              >
                {products?.map((product) => (
                  <Option key={product.id} value={product.id}>
                    {product.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t('booklets.field.redeemProduct', 'Redeem product')}
              name="redeemProductId"
              rules={[{ required: true, message: t('fields.required') }]}
            >
              <Select
                loading={loadingProducts}
                placeholder={t('fields.select product')}
                showSearch
                optionFilterProp="children"
                disabled={!canUpdateBooklets}
              >
                {products?.map((product) => (
                  <Option key={product.id} value={product.id}>
                    {product.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {canUpdateBooklets && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!isDirty}
                  loading={loadingProducts}
                >
                  {t('app.save')}
                </Button>
              </Form.Item>
            )}
          </Form>

          <Divider />
          <Title level={3}>{t('booklets.transactions', 'Transactions')}</Title>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Form form={groupsFiltersForm} layout="inline" onFinish={onSubmitGroupsFilters}>
              <Form.Item label={t('fields.customer code', 'Customer code')} name="customerCode">
                <Input allowClear placeholder={t('fields.search', 'Search')} style={{ width: 200 }} />
              </Form.Item>

              {/* <Form.Item label={t('fields.sku', 'SKU')} name="skuCode">
                <Input allowClear placeholder={t('fields.search', 'Search')} style={{ width: 160 }} />
              </Form.Item> */}

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  {t('app.search', 'Search')}
                </Button>
              </Form.Item>

              <Form.Item>
                <Button onClick={resetGroupsFilters}>{t('app.reset', 'Reset')}</Button>
              </Form.Item>
            </Form>

            <CustomButton type="primary" onClick={() => setIsExportModalVisible(true)}>
              <Icon icon={ICONS.UPLOAD} size={24} color={'#ffffff'} />
              <span className="btn-text">{t('app.export')}</span>
            </CustomButton>
          </div>

          <Table
            columns={groupColumns}
            dataSource={isTransactionGroupsLoading || hasGroupsError ? [] : groupItems}
            rowKey={groupRowKey}
            style={{ height: 'auto', marginTop: 16 }}
            onChange={handleGroupsTableChange}
            loading={isTransactionGroupsLoading}
            expandable={{
              expandedRowKeys: expandedGroupKey ? [expandedGroupKey] : [],
              onExpand: (expanded, record) => {
                const key = groupRowKey(record)

                if (!expanded) {
                  setExpandedGroupKey(null)
                  setExpandedGroup(null)
                  setTxCurrentPage(1)
                  dispatch(
                    setBookletTransactions({
                      result: [],
                      total: 0,
                    }),
                  )
                  return
                }

                setExpandedGroupKey(key)
                setExpandedGroup({
                  userId: record.userId,
                  skuCode: record.skuCode,
                })
                setTxCurrentPage(1)
              },
              expandedRowRender: (record) => {
                const isActiveGroup =
                  expandedGroup?.userId === record.userId && expandedGroup?.skuCode === record.skuCode

                return (
                  <div style={{ padding: 12, background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      <Button
                        type={txTypeFilter === 'ALL' ? 'primary' : 'default'}
                        onClick={() => {
                          setTxCurrentPage(1)
                          setTxTypeFilter('ALL')
                        }}
                      >
                        {t('booklets.transactionFilters.all', 'All')}
                      </Button>
                      <Button
                        type={txTypeFilter === 'PURCHASE' ? 'primary' : 'default'}
                        onClick={() => {
                          setTxCurrentPage(1)
                          setTxTypeFilter('PURCHASE')
                        }}
                      >
                        {t('booklets.transactionFilters.purchase', 'Purchase')}
                      </Button>
                      <Button
                        type={txTypeFilter === 'REDEEM' ? 'primary' : 'default'}
                        onClick={() => {
                          setTxCurrentPage(1)
                          setTxTypeFilter('REDEEM')
                        }}
                      >
                        {t('booklets.transactionFilters.redeem', 'Redeem')}
                      </Button>

                      <Button
                        type={txTypeFilter === 'EXPIRED' ? 'primary' : 'default'}
                        onClick={() => {
                          setTxCurrentPage(1)
                          setTxTypeFilter('EXPIRED')
                        }}
                      >
                        {t('booklets.expiry.expired', 'Expired')}
                      </Button>

                      <Button onClick={resetTransactionsFilters}>{t('app.reset', 'Reset')}</Button>
                    </div>

                    <Table
                      columns={txColumns}
                      dataSource={
                        !isActiveGroup || isTransactionsLoading || hasTransactionsError ? [] : txItems
                      }
                      rowKey={(r) => r.id}
                      size="small"
                      style={{ marginTop: 12 }}
                      onChange={handleTransactionsTableChange}
                      loading={isActiveGroup && isTransactionsLoading}
                      pagination={{
                        pageSize: txPageSize,
                        current: txCurrentPage,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        total: !isActiveGroup || isTransactionsLoading || hasTransactionsError ? 0 : txTotal,
                        showSizeChanger: true,
                      }}
                    />
                  </div>
                )
              },
            }}
            pagination={{
              pageSize: groupsPageSize,
              current: groupsCurrentPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              total: isTransactionGroupsLoading || hasGroupsError ? 0 : groupTotal,
              showSizeChanger: true,
            }}
          />
        </div>
      </div>
      <ExportBookletsModal
        isVisible={isExportModalVisible}
        handleCancel={() => setIsExportModalVisible(false)}
        fileDownloadedHandler={() => setIsExportModalVisible(false)}
      />
    </Page>
  )
}

export default BookletsView
